import { CloseOutlined } from '@ant-design/icons';
import type { RangePickerProps } from 'antd/es/date-picker';
import { validate } from 'email-validator';
import { useState } from 'react';
import styled from 'styled-components';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import StyledDatePicker from '@/components/DatePicker';
import { AttrTextInput } from '@/components/Inputs';
import FlexibleTitleInput from '@/components/Inputs/FlexibleTitle';
import { AttrText } from '@/components/Typography/NamedTypo';
import CompanyDropdown from '@/features/Account/components/CompanyDropdown';
import AttrSelectDropdown from '@/features/Commons/Dropdown/AttrSelectDropdown';
import useCustomerAttributes from '@/hooks/domain/useCustomerAttributes';
import useKeymap from '@/hooks/pure/useKeymap';
import useStates from '@/hooks/pure/useStates';
import useCustomerStore from '@/stores/CustomerStore';
import useToastStore from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { detailModalStyle } from '@/styles/style';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';
import { timeOut } from '@/utils/util';

type State = {
  name: string;
  company_info: CompanyInfoWithAlias | null;
  job_function: string | null;
  email: string;
  phone_number: string;
  description: string;
  reference_link: string;
  country: string | null;
  type: string | null;
  id: string;
  sns_id: string;
  usage?: number;
  agent: string | null;
  contact_plan: string | null;
  contact_stage: string | null;
  date: string;
};

const CreateCustomerModal = () => {
  const {
    addNewCustomer,
    setCreateModalStatus,
    fetchCustomerList,
    customerMap,
  } = useCustomerStore((state) => ({
    addNewCustomer: state.addNewCustomer,
    setCreateModalStatus: state.setCreateModalStatus,
    customerMap: state.customerMap,
    fetchCustomerList: state.fetchCustomerList,
  }));

  const { updateWorkspaceFieldOptions } = useWorkspaceStore(
    ({ updateWorkspaceFieldOptions }) => ({ updateWorkspaceFieldOptions }),
  );

  const {
    jobFunctionOptions,
    typeOptions,
    countryOptions,
    agentOptions,
    planOptions,
    stageOptions,
  } = useCustomerAttributes();

  const [loading, setLoading] = useState(false);

  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));
  const { state, setValue } = useStates<State>({
    name: '',
    company_info: null,
    job_function: null,
    email: '',
    phone_number: '',
    description: '',
    reference_link: '',
    country: null,
    type: null,
    id: '',
    sns_id: '',
    usage: undefined,
    agent: null,
    contact_plan: null,
    contact_stage: null,
    date: getTimezoneDayjs().format(DATE_FORMAT.base),
  });

  const closeModal = () => {
    setCreateModalStatus(false);
  };

  const onSave = async () => {
    try {
      if (state.name === '') {
        addDefaultToast('Name must be filled', 'fail');
        return;
      }
      for (const customer of Object.values(customerMap)) {
        if (state.email && customer.email?.includes(state.email)) {
          addDefaultToast('This email already exists.', 'fail');
          return;
        }
      }

      if (state.email && !validate(state.email)) {
        addDefaultToast('Invalid email format.', 'fail');
        return;
      }
      const req: PostCustomerParams = {
        feedbacks: [],
        name: state.name,
        email: state.email.trim() !== '' ? state.email : undefined,
        job_function: state.job_function || undefined,
        company:
          state.company_info !== null ? state.company_info.alias : undefined,
        phone: state.phone_number,
        desc: state.description,
        reference_link: state.reference_link,
        country: state.country || undefined,
        type: state.type || undefined,
        external_id: state.id,
        sns_id: state.sns_id,
        usage: state.usage,
        agent: state.agent || undefined,
        contact_plan: state.contact_plan || undefined,
        contact_stage: state.contact_stage || undefined,
        date:
          getTimezoneDayjs(state.date).format('YYYY-MM-DDTHH:mm:ssZ') ||
          undefined,
      };

      setLoading(true);
      await addNewCustomer(req);
      await timeOut(2000);
      await fetchCustomerList();
      setLoading(false);
      setCreateModalStatus(false);
    } catch (e) {
      console.error(e);
      addDefaultToast('Create fail', 'fail');
    }
  };

  useKeymap('Escape', closeModal);

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current > getTimezoneDayjs().startOf('day');
  };

  return (
    <ModalStyle status={true}>
      <ContentStyle>
        <Flex j="flex-end">
          <IconButton
            icon={<CloseOutlined />}
            onClick={closeModal}
            tooltip="Close"
            shortcut="ESC"
          />
        </Flex>
        <Space h={5} />
        <FlexibleTitleInput
          placeholder="New Contact"
          name="name"
          value={state.name}
          onChange={(e) => setValue('name', e.target.value)}
        />
        <Space h="21px" />
        <VFlex g={5}>
          <Flex className="property-row">
            <AttrText className="property-label">Account</AttrText>
            <CompanyDropdown
              clearable
              name={`create-customer-company-dropdown`}
              company_info={state.company_info}
              changeCompany={(company) => {
                setValue(
                  'company_info',
                  company
                    ? {
                        alias: company?.alias,
                        name: company?.name,
                      }
                    : null,
                );
              }}
              view="detail"
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Job Function</AttrText>
            <AttrSelectDropdown
              name={`customer-create-position-dropdown`}
              view="create"
              selected={state.job_function || null}
              options={jobFunctionOptions}
              onSelect={(key) => setValue('job_function', key)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(
                  1,
                  'job_function',
                  optionName,
                );
                setValue('job_function', optionName);
              }}
              onClear={() => setValue('job_function', null)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Email address</AttrText>
            <AttrTextInput
              placeholder="Enter email address"
              value={state.email}
              onChange={(e) => setValue('email', e.target.value)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Phone number</AttrText>
            <AttrTextInput
              placeholder="Enter phone number"
              value={state.phone_number}
              onChange={(e) => setValue('phone_number', e.target.value)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Description</AttrText>
            <AttrTextInput
              placeholder="Enter Description"
              value={state.description}
              onChange={(e) => setValue('description', e.target.value)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Reference link</AttrText>
            <AttrTextInput
              placeholder="Enter reference link"
              value={state.reference_link}
              onChange={(e) => setValue('reference_link', e.target.value)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Country</AttrText>
            <AttrSelectDropdown
              name={`customer-create-country-dropdown`}
              view="create"
              selected={state.country || null}
              options={countryOptions}
              onSelect={(key) => setValue('country', key)}
              onClear={() => setValue('country', null)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Type</AttrText>
            <AttrSelectDropdown
              name={`customer-create-type-dropdown`}
              view="create"
              selected={state.type || null}
              options={typeOptions}
              onSelect={(key) => setValue('type', key)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(
                  1,
                  'contact_type',
                  optionName,
                );
                setValue('type', optionName);
              }}
              onClear={() => setValue('type', null)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Plan</AttrText>
            <AttrSelectDropdown
              name={`customer-create-plan-dropdown`}
              view="create"
              selected={state.contact_plan || null}
              options={planOptions}
              onSelect={(key) => setValue('contact_plan', key)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(
                  1,
                  'contact_plan',
                  optionName,
                );
                setValue('contact_plan', optionName);
              }}
              onClear={() => setValue('contact_plan', null)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Stage</AttrText>
            <AttrSelectDropdown
              name={`customer-create-stage-dropdown`}
              view="create"
              selected={state.contact_stage || null}
              options={stageOptions}
              onSelect={(key) => setValue('contact_stage', key)}
              onCreate={async (optionName) => {
                await updateWorkspaceFieldOptions(
                  1,
                  'contact_stage',
                  optionName,
                );
                setValue('contact_stage', optionName);
              }}
              onClear={() => setValue('contact_stage', null)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">ID</AttrText>
            <AttrTextInput
              placeholder="Enter ID"
              value={state.id}
              onChange={(e) => setValue('id', e.target.value)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">SNS ID</AttrText>
            <AttrTextInput
              placeholder="Enter SNS ID"
              value={state.sns_id}
              onChange={(e) => setValue('sns_id', e.target.value)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Usage</AttrText>
            <AttrTextInput
              type="number"
              placeholder="Enter usage volume"
              value={state.usage}
              onChange={(e) => {
                if (Number(e.target.value) < 0) {
                  addDefaultToast(
                    'Usage must be equal to or greater than 0',
                    'fail',
                  );
                  return;
                }
                setValue('usage', Number(e.target.value));
              }}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Agent</AttrText>
            <AttrSelectDropdown
              name={`customer-create-agent-dropdown`}
              view="create"
              selected={state.agent || null}
              options={agentOptions}
              onSelect={(key) => setValue('agent', key)}
              onClear={() => setValue('agent', null)}
            />
          </Flex>
          <Flex className="property-row">
            <AttrText className="property-label">Date</AttrText>
            <StyledDatePicker
              name="date"
              onChange={(_, dataString) => {
                setValue('date', dataString);
              }}
              disabledDate={disabledDate}
              getPopupContainer={(triggerNode) => {
                return triggerNode.parentNode as HTMLDivElement;
              }}
              value={state.date ? getTimezoneDayjs(state.date) : null}
              format={DATE_FORMAT.readable}
            />
          </Flex>
        </VFlex>
      </ContentStyle>
      <Space h="22px" />
      <Flex j="flex-end" style={{ marginRight: '32px' }}>
        <Button onClick={onSave} isLoading={loading} sizeType="m">
          Save
        </Button>
      </Flex>
    </ModalStyle>
  );
};

export default CreateCustomerModal;

const ModalStyle = styled(FadeDiv)`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: 0;
  ${detailModalStyle}
`;

const ContentStyle = styled.div`
  padding: 28px 32px 0 32px;

  .property-row {
    gap: 10px;
    align-items: center;
    height: 32px;
  }

  .property-label {
    width: 90px;
    height: 24px;
    line-height: 24px;
  }

  .company-selector-wrapper {
    height: 38px;
    position: relative;
    cursor: pointer;
    background-color: ${(props) => props.theme.colors.gray100};
    transition: all 0.2s;
    padding: 0 11px;
    border-radius: 4px;
    .edit-icon {
      opacity: 0;
      transform: 0.2s;
    }
    &:hover {
      background-color: ${(props) => props.theme.colors.gray200};
      .edit-icon {
        opacity: 1;
      }
    }
  }
`;
